<script setup lang="ts">
import { FadeTransition, isNumeric } from '#shared'
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query'
import { capitalize, computed, useTemplateRef } from 'vue'
import { useRoute, type RouteLocationNormalizedLoaded } from 'vue-router'
import { getChatQueryOptions, useChatId } from '#entities/chat'
import {
  getFreshMessagesQueryOptions,
  getMessagesQueryOptions,
  type Message,
  type MessagesQueryParams,
} from '#entities/message'
import { getMessagePositionQueryOptions } from '#entities/message/state/message-position-query'
import { useInfiniteScroll } from '#shared/lib/misc/use-infinite-scroll'
import { useMessageReading } from '../lib/use-message-reading'
import MessageListAuthRequired from './message-list-auth-required.vue'
import MessageListContent from './message-list-content.vue'
import MessageListErrorState from './message-list-error-state.vue'
import MessageListSkeleton from './message-list-skeleton.vue'
import MessageListUnreadCount from './message-list-unread-count.vue'
import MessagesListEmptyState from './messages-list-empty-state.vue'

const { variant = 'regular' } = defineProps<{ variant?: 'regular' | 'compact' }>()

const chatId = useChatId()
const chatQuery = useQuery(getChatQueryOptions(chatId.value))
const chat = chatQuery.data

const freshMessagesQuery = useQuery(getFreshMessagesQueryOptions(chatId))

const route = useRoute() as RouteLocationNormalizedLoaded | undefined
const targetMessageId = computed(() =>
  isNumeric(route?.query.targetMessageId) ? Number(route?.query.targetMessageId) : -1
)
const isTargetMessageFresh = computed(() => {
  if (targetMessageId.value === -1) return false
  return freshMessagesQuery.data.value?.some(message => message.id === targetMessageId.value) ?? false
})
const navnonce = computed(() => route?.query.navnonce)
const targetPositionQuery = useQuery({
  ...getMessagePositionQueryOptions(chatId, targetMessageId),
  enabled: targetMessageId.value !== -1 && !isTargetMessageFresh.value,
})
const canLoadMessages = computed(() => {
  if (targetMessageId.value === -1) return true
  return !targetPositionQuery.isPending.value
})
const messageQueryParams = computed(() => {
  const messageQueryParams: MessagesQueryParams = {}
  if (targetMessageId.value !== -1 && !isTargetMessageFresh.value) {
    messageQueryParams.startAfter = targetPositionQuery.data.value
  }
  return messageQueryParams
})

const messagesQuery = useInfiniteQuery({
  ...getMessagesQueryOptions(chatId, messageQueryParams),
  enabled: canLoadMessages,
})
const areFreshMessagesDisplayed = computed(() => !messagesQuery.hasPreviousPage.value)
const messages = computed(() => {
  const oldMessages = messagesQuery.data.value?.pages.flatMap(page => page.data).reverse() ?? []
  const freshMessages = freshMessagesQuery.data.value ?? []

  const messages: Message[] = [...oldMessages]
  if (areFreshMessagesDisplayed.value) {
    messages.push(...freshMessages)
  }
  return messages
})

const scrollerEl = useTemplateRef<HTMLDivElement>('scrollerEl')
useInfiniteScroll(scrollerEl, {
  direction: 'both',
  canLoadUp: computed(() => messagesQuery.hasNextPage.value && !messagesQuery.isFetching.value),
  canLoadDown: computed(() => messagesQuery.hasPreviousPage.value && !messagesQuery.isFetching.value),
  loadUp: () => messagesQuery.fetchNextPage(),
  loadDown: () => messagesQuery.fetchPreviousPage(),
})

const rootEl = useTemplateRef<HTMLDivElement>('rootEl')

useMessageReading(scrollerEl)

const onUnreadCountClick = () => {
  scrollerEl.value?.scrollTo({ top: scrollerEl.value.scrollHeight, behavior: 'smooth' })
}
</script>

<template>
  <div :class="$style.root" ref="rootEl" data-behavior="message-list">
    <FadeTransition>
      <div v-if="chat?.isProtected && !chat.signedAt" :class="$style.contentWrap">
        <MessageListAuthRequired />
      </div>

      <div v-else-if="messagesQuery.isPending.value" :class="$style.contentWrap">
        <MessageListSkeleton :variant="variant" />
      </div>

      <div v-else-if="messagesQuery.isError.value" :class="$style.contentWrap" data-behavior="message-list">
        <MessageListErrorState @refresh="messagesQuery.refetch" />
      </div>

      <div v-else-if="messages.length === 0" :class="$style.contentWrap">
        <MessagesListEmptyState />
      </div>

      <div v-else :class="[$style.contentWrap, $style.contentWrapScroller]" ref="scrollerEl">
        <MessageListContent
          :messages="messages"
          :chatId="chatId"
          :variant="variant"
          :parentEl="rootEl"
          :scrollerEl="scrollerEl"
          :targetMessageId="targetMessageId"
          :navnonce="navnonce"
          :areFreshMessagesDisplayed="areFreshMessagesDisplayed"
        />
      </div>
    </FadeTransition>

    <MessageListUnreadCount
      v-if="chat?.unreadCount && chat.meta?.unreadStartsAt"
      :class="[$style.unreadCount, $style['unreadCount' + capitalize(variant)]]"
      :unreadCount="chat.unreadCount"
      :unreadUpdatedAt="chat.meta.unreadStartsAt"
      @click="onUnreadCountClick"
    />
  </div>
</template>

<style lang="scss" module>
@use '#mixins/scrollbar' as scrollbar;

.root {
  width: 100%;
  height: 100%;
  min-height: 0;
  position: relative;
}

.unreadCount {
  position: absolute;
  z-index: 1;
}

.unreadCountRegular {
  top: 15px;
  left: 20px;
  right: 20px;
}

.unreadCountCompact {
  top: 10px;
  left: 10px;
  right: 10px;
}

.contentWrap {
  width: 100%;
  height: 100%;
  min-height: 0;
  position: absolute;
  top: 0;
  left: 0;
}

.contentWrapScroller {
  overflow: auto;
  @include scrollbar.thick(#1f1f22);
}
</style>
